import { defineStore } from "pinia";
import { ref } from "vue";

import {
  getBuildingProjectLookups,
  getDeclaredUnits,
  getProductTypes,
  getSpecificationForms,
  getLifeCycleStages,
  getIndicatorTypeNames,
  getMassUnits,
  getIndicatorTypes,
  getTagsTypesForms,
  getDataSources,
  getMaterialTenants,
  getCanMapDictionaries,
} from "@/api/services/Enums";

import type {
  ProductType,
  SpecificationForm,
  LifeCycleStage,
  DeclaredUnit,
  BuildingProjectLookups,
  IndicatorTypeName,
  MassPerUnit,
  IndicatorUnitsMap,
  Tag,
  DataSource,
  Tenant,
  CanMapDictionaries,
  UnitOption,
} from "./types/enum";

export const useEnum = defineStore("enum", () => {
  // STATE
  const productTypes = ref<ProductType[]>([]);
  const specificationForms = ref<SpecificationForm[]>([]);
  const lifeCycleStages = ref<LifeCycleStage[]>([]);
  const declaredUnits = ref<DeclaredUnit[]>([]);
  const buildingProjectLookups = ref<BuildingProjectLookups | null>(null);

  const indicatorTypeNames = ref<IndicatorTypeName[]>([]);
  const indicatorUnits = ref<IndicatorUnitsMap>({} as IndicatorUnitsMap);

  const massUnits = ref<MassPerUnit[]>([]);
  const tagsTypesForms = ref<Tag[]>([]);
  const datasources = ref<DataSource[]>([]);
  const tenants = ref<Tenant[] | null>(null);
  const canMapDictionaries = ref<CanMapDictionaries | null>(null);

  // ACTIONS
  const fetchProductTypes = async () => {
    const data = await getProductTypes();

    const arr: ProductType[] = Object.values(data);
    productTypes.value = [{ Enum: "CUSTOM", DisplayName: "Custom" }, ...arr];
  };

  const fetchSpecificationForms = async () => {
    specificationForms.value = await getSpecificationForms();
  };

  const fetchLifeCycleStages = async () => {
    lifeCycleStages.value = await getLifeCycleStages();
  };

  const fetchDeclaredUnits = async () => {
    declaredUnits.value = await getDeclaredUnits();
  };

  const fetchBuildingProjectLookups = async () => {
    buildingProjectLookups.value = await getBuildingProjectLookups();
  };

  const fetchIndicatorTypeNames = async () => {
    indicatorTypeNames.value = await getIndicatorTypeNames();
  };

  const fetchIndicatorTypes = async () => {
    indicatorUnits.value = await getIndicatorTypes();
  };

  const fetchMassUnits = async () => {
    massUnits.value = await getMassUnits();
  };

  const fetcTagsTypesForms = async () => {
    const data = await getTagsTypesForms();
    if (data) {
      tagsTypesForms.value = data as Tag[];
    }
  };

  const fetchDataSources = async () => {
    datasources.value = await getDataSources();
  };

  const fetchMaterialTenants = async () => {
    tenants.value = await getMaterialTenants();
  };

  const fetchCanMapDictionaries = async () => {
    const data = await getCanMapDictionaries();
    if (data && !("status" in data)) {
      canMapDictionaries.value = data as CanMapDictionaries;
    } else {
      canMapDictionaries.value = null;
    }
  };

  const unitOptions: UnitOption[] = [
    { unit: "kg/m", displayName: "Meter", declaredUnit: 3, massUnit: 0 },
    {
      unit: "kg/m²",
      displayName: "Square meters",
      declaredUnit: 4,
      massUnit: 1,
    },
    {
      unit: "kg/m³",
      displayName: "Cubic meters",
      declaredUnit: 5,
      massUnit: 2,
    },
    { unit: "kg/pcs", displayName: "Piece", declaredUnit: 6, massUnit: 3 },
  ];
  const getMassUnit = (index: number) => unitOptions[index] ?? null;

  return {
    // state
    productTypes,
    specificationForms,
    lifeCycleStages,
    declaredUnits,
    buildingProjectLookups,
    indicatorTypeNames,
    indicatorUnits,
    massUnits,
    tagsTypesForms,
    datasources,
    tenants,
    canMapDictionaries,
    unitOptions,

    // actions
    fetchProductTypes,
    fetchSpecificationForms,
    fetchLifeCycleStages,
    fetchDeclaredUnits,
    fetchBuildingProjectLookups,
    fetchIndicatorTypeNames,
    fetchIndicatorTypes,
    fetchMassUnits,
    fetcTagsTypesForms,
    fetchDataSources,
    fetchMaterialTenants,
    fetchCanMapDictionaries,

    // getter
    getMassUnit,
  };
});
